import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Trading → Trade Restrictions / Do Not Trade → Add Trade Restriction /
 * Do Not Trade / Remove Trade Restriction.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version active 2026-10-04). Add Trade Restriction and Do Not Trade: screen
 * "Trade Restrictions / Do Not Trade" (shared questions), then "Add Trade
 * Restriction" or "Do Not Trade". Remove Trade Restriction: screen "Remove
 * Trade Restriction" only. Each sub-category is written against the screens
 * named for it. Shared chrome and policy copy live in GuideKit.js.
 */

const FEE_YES = 'Yes (Some or all of these accounts pay a management fee, we need to re-assign fees from this account to another account)';
const FEE_NO = 'No (All of these accounts are currently not billed, I understand this Case will be NIGOed if there is a fee schedule associated with any of the selected accounts)';
const FEE_NA = 'Not Applicable (All of the clients accounts are transferring out)';
const END_YES = 'Yes (I am adding an expiration date)';
const END_NO = 'No (Expiration date unknown, we will reach back out to you when ready, and understand that it will be in effect until then)';

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Trade Restrictions / Do Not Trade',
    subCategory: 'Add Trade Restriction, Do Not Trade or Remove Trade Restriction',
    subject: subjectRow('Do not trade - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>Add Trade Restriction and Do Not Trade ask the same questions.</strong> Remove Trade Restriction
        skips them and goes straight to the account table.
      </>
    ),
  }),
  {
    title: 'Add Trade Restriction or Do Not Trade: answer the restriction questions',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>Trade Restrictions / Do Not Trade</strong> screen asks why, at what level, whether it expires,
        and whether the accounts pay fees. Each question appears once the one above it is answered, and every
        question is required once it appears. Click Next.
      </p>
    ),
  },
  {
    title: 'Describe the request and choose the accounts',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          <strong>Add Trade Restriction or Do Not Trade:</strong> the next screen opens with a single box,{' '}
          <em>Please provide all relevant information for the Trading Team...</em> Fill it in first - the account
          table does not appear until you do. Then tick the accounts the restriction applies to.
        </p>
        <p style={{ margin: 0 }}>
          <strong>Remove Trade Restriction:</strong> the <strong>Remove Trade Restriction</strong> screen shows
          the account table first, then the same details box. Tick the accounts to lift the restriction from and
          fill in the box.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the table - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

const QUESTIONS = [
  {
    screen: 'Trade Restrictions / Do Not Trade (Add Trade Restriction and Do Not Trade only)',
    q: 'Is the Restriction / Do Not Trade due to any of the following reasons?',
    when: 'Always - this is the first question',
    options: [
      'Client Directed Hold (Client Requested)',
      'Tax Sensitivity (Concentrated or Legacy positions)',
      'Employer Restricted (Employee stock, blackout window, 10b5-1)',
      'Pending account transfer (Client is moving all of their assets)',
      'Legal Hold (Divorce, litigation, estate, etc.)',
      'Diminished Capacity Concerns',
      'Deceased Client',
      'None of the Above',
    ],
  },
  {
    q: 'Is this Restriction / Do Not Trade on the Household Level (All Accounts under the Household), Account Level (Specific Accounts only), or Account/Asset Level (Specific Assets within an Account)?',
    when: 'Once a reason is chosen',
    options: [
      "Household Level (Do Not Trade / Add Restriction to all of this Client's accounts)",
      'Account Level (Do Not Trade / Add Restriction only to specific accounts)',
      'Account/Asset Level (Do Not Trade / Add Restriction only to specific assets within a specific account)',
    ],
    note: 'For Account/Asset Level, name the specific assets in the details box on the next screen.',
  },
  {
    q: 'Does this Restriction / Do Not Trade have an End Date / Expiration?',
    when: 'Once the level is chosen',
    options: [END_YES, END_NO],
    note: 'Without an end date the restriction stays in effect until you reach back out to Trading.',
  },
  {
    q: 'Trade Restriction / Do Not Trade Expiration Date',
    when: `Only if you chose ${END_YES}`,
  },
  {
    q: 'Are any of the restricted / blocked accounts currently paying fees?',
    when: 'Once you enter the expiration date, or straight away if there is no end date',
    options: [FEE_YES, FEE_NO, FEE_NA],
    note: 'Answering No when any selected account has a fee schedule gets the case NIGOed - check billing before you answer.',
    warn: true,
  },
  {
    q: 'Please select the account you would like to charge the fees to in its place...',
    when: 'Only if some or all of the accounts pay a management fee',
    note: 'A drop-down of the household’s other accounts that can carry the fee: active, balance of $100 or more, a fee schedule other than Unassigned, and not themselves restricted, blocked or marked Do Not Trade. Cancelled (CNX), Distribution and Contribution sleeves are left out.',
  },
  {
    screen: 'Add Trade Restriction · Do Not Trade',
    q: 'Please provide all relevant information for the Trading Team...',
    when: 'Always - first on the screen',
    note: 'Required. This becomes the case description. Include anything the questions did not cover, such as specific assets for an Account/Asset Level restriction.',
  },
  {
    q: 'Select Accounts (Add Trade Restriction) · Select Accounts (Do Not Trade)',
    when: 'Once the details box has something in it',
    note: 'A table with Account Number, Account Details, Model, Cash Balance and Balance. Tick as many rows as apply.',
  },
  {
    q: 'Acknowledgement',
    when: 'Under the notice',
    options: ['I have read, understand, and agree to the above information'],
  },
  {
    screen: 'Remove Trade Restriction',
    q: 'Select Accounts (Remove Trade Restriction)',
    when: 'Remove Trade Restriction - always, first on the screen',
    note: 'A table with Name (the account number), Account Details, Model, Cash Balance and Balance. Tick each account the restriction should come off.',
  },
  {
    q: 'Please provide all relevant information for the Trading Team...',
    when: 'Remove Trade Restriction - always',
    note: 'Required. This becomes the case description. Say which restriction is ending and whether any trading should follow.',
  },
  {
    q: 'Acknowledgement',
    when: 'Remove Trade Restriction - under the notice',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Trade Restrictions / Do Not Trade'),
  {
    h: "The account I want to move the fee to isn't in the drop-down",
    p: [
      'The fee drop-down only lists accounts under the same household that are active, hold $100 or more, have a fee schedule other than Unassigned, and are not restricted, trading blocked or marked Do Not Trade themselves. If none qualifies, email tradingops@prosperitycapitaladvisors.com before you submit.',
    ],
  },
  {
    h: "The account table hasn't appeared",
    p: ['On Add Trade Restriction and Do Not Trade it stays hidden until the details box at the top of the screen has something in it.'],
  },
  troubleSleeveRows('These tables are not filtered - every sleeve under the household is listed, including inactive and cancelled (CNX) sleeves, so check the account number before you tick it.'),
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function TradeRestrictions() {
  return (
    <GuidePage
      title="Trade Restriction and Do Not Trade Requests"
      icon="slash-circle"
      path="Trading → Trade Restrictions / Do Not Trade → Add, Do Not Trade or Remove"
      minutes={4}
      intro="How to ask the Prosperity Trading team to add a trade restriction, put accounts on Do Not Trade, or lift a restriction - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. Removing a restriction needs only the accounts and a description.</Para>
        <Bullets items={[
          <><strong>The reason</strong> for the restriction - client directed, tax sensitivity, employer restriction, pending transfer, legal hold, diminished capacity, deceased client, or other.</>,
          <><strong>The level</strong> - the whole household, specific accounts, or specific assets within an account (and which assets).</>,
          <><strong>The end date</strong>, if the restriction expires.</>,
          <><strong>Which accounts pay a management fee</strong>, and which other account in the household should be charged instead.</>,
          <><strong>The accounts involved</strong>, all under the same household. You pick them from a list, so you do not need account numbers memorised.</>,
        ]} />
        <Callout tone="warn">
          If any account you restrict has a fee schedule and you answer that none of them pay fees, the case is
          NIGOed. Check billing first.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="trading" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not legal or compliance advice on when a
        restriction is required, and it does not describe how Trading applies one at the custodian.
      </Footnote>
    </GuidePage>
  );
}
